/*
 * a11y-carousel-pause.js
 * WCAG 2.2.2 (Pause, Stop, Hide) — adds an accessible Pause/Play control to
 * every auto-advancing carousel on the site so users can stop the motion.
 *
 * Covers two mechanisms with one script:
 *   1. Swiper autoplay carousels  — meet-the-team2 (homepage), brief_with_stats
 *      (initiative pages), brand-details (portfolio detail page).
 *   2. Focal <slide-show auto-play> — the thesis / ecosystem / supply slideshows.
 *
 * The button mirrors the hero-video control: 44x44 circular, dark translucent
 * background, white icon, bottom-right, visible focus ring. Honours
 * prefers-reduced-motion by auto-pausing on load. Fully defensive: any single
 * carousel that fails is skipped without affecting the others.
 */
(function () {
  'use strict';

  var PAUSE_ICON = '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false"><rect x="6" y="5" width="4" height="14" rx="1"></rect><rect x="14" y="5" width="4" height="14" rx="1"></rect></svg>';
  var PLAY_ICON = '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false"><path d="M8 5v14l11-7z"></path></svg>';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Create the toggle button. `noun` is used in the accessible label, e.g.
  // "Pause carousel" / "Play slideshow".
  function makeButton(noun) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'a11y-carousel-toggle';
    btn.innerHTML = PAUSE_ICON;
    btn.setAttribute('aria-pressed', 'false');
    btn.setAttribute('aria-label', 'Pause ' + noun);

    // render(paused): keep icon, label and pressed-state in sync.
    btn._render = function (paused) {
      btn.innerHTML = paused ? PLAY_ICON : PAUSE_ICON;
      btn.setAttribute('aria-pressed', String(paused));
      btn.setAttribute('aria-label', (paused ? 'Play ' : 'Pause ') + noun);
    };
    return btn;
  }

  function ensureRelative(el) {
    try {
      if (getComputedStyle(el).position === 'static') {
        el.style.position = 'relative';
      }
    } catch (e) { /* no-op */ }
  }

  /* ---------------------------------------------------------------------- *
   * 1. Swiper autoplay carousels                                            *
   * ---------------------------------------------------------------------- */
  function enhanceSwiper(el) {
    if (!el || el.getAttribute('data-a11y-pause') === '1') return;
    var sw = el.swiper;
    if (!sw || !sw.autoplay) return;

    // Only enhance carousels that actually autoplay.
    var apParams = sw.params && sw.params.autoplay;
    var hasAutoplay = !!(apParams && apParams.enabled) || !!sw.autoplay.running;
    if (!hasAutoplay) return;

    // Only enhance carousels that actually move — a single-slide carousel has
    // nothing to advance to, so a pause control would sit on static content.
    var realSlides = el.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)').length;
    if (realSlides < 2) return;

    el.setAttribute('data-a11y-pause', '1');
    ensureRelative(el);

    var noun = 'carousel';
    var btn = makeButton(noun);
    var paused = false;

    function pause() {
      try { sw.autoplay.stop(); } catch (e) {}
      paused = true;
      btn._render(true);
    }
    function play() {
      try { sw.autoplay.start(); } catch (e) {}
      paused = false;
      btn._render(false);
    }
    btn.addEventListener('click', function () {
      if (paused) { play(); } else { pause(); }
    });

    el.appendChild(btn);

    // Honour reduced-motion: stop the auto-advance on load.
    if (prefersReducedMotion()) {
      pause();
    } else {
      btn._render(false);
    }
  }

  function scanSwiper() {
    var nodes = document.querySelectorAll('.swiper');
    for (var i = 0; i < nodes.length; i++) {
      enhanceSwiper(nodes[i]);
    }
  }

  /* ---------------------------------------------------------------------- *
   * 2. Focal <slide-show auto-play>                                         *
   * ---------------------------------------------------------------------- */
  function enhanceFocal(el) {
    if (!el || el.getAttribute('data-a11y-pause') === '1') return;
    // The custom element must be upgraded and expose its player methods.
    if (typeof el.pausePlayer !== 'function' || typeof el.startPlayer !== 'function') return;
    // Needs more than one slide to actually move.
    var slides = el.querySelectorAll('slide-show-item');
    if (slides.length < 2) return;

    el.setAttribute('data-a11y-pause', '1');
    ensureRelative(el);

    // Guard the component's internal restarts: select() calls startPlayer()
    // after each transition, which would override a user's pause. Override
    // startPlayer so it becomes a no-op while the user has paused.
    var origStart = el.startPlayer.bind(el);
    el._a11yPaused = false;
    el.startPlayer = function () {
      if (el._a11yPaused) return;
      return origStart();
    };

    var noun = 'slideshow';
    var btn = makeButton(noun);

    function pause() {
      el._a11yPaused = true;
      try { el.pausePlayer(); } catch (e) {}
      btn._render(true);
    }
    function play() {
      el._a11yPaused = false;
      try { origStart(); } catch (e) {}
      btn._render(false);
    }
    btn.addEventListener('click', function () {
      if (el._a11yPaused) { play(); } else { pause(); }
    });

    el.appendChild(btn);

    if (prefersReducedMotion()) {
      pause();
    } else {
      btn._render(false);
    }
  }

  function scanFocal() {
    var nodes = document.querySelectorAll('slide-show[auto-play]');
    for (var i = 0; i < nodes.length; i++) {
      enhanceFocal(nodes[i]);
    }
  }

  /* ---------------------------------------------------------------------- *
   * Boot: carousels initialise asynchronously (Swiper waits for its CDN     *
   * lib; the Focal element upgrades on its own schedule). Poll for a while  *
   * so late-initialising carousels still get a control. Idempotent.         *
   * ---------------------------------------------------------------------- */
  function scan() {
    try { scanSwiper(); } catch (e) {}
    try { scanFocal(); } catch (e) {}
  }

  function boot() {
    scan();
    var tries = 0;
    var iv = setInterval(function () {
      scan();
      if (++tries > 30) clearInterval(iv); // ~12s of coverage
    }, 400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
